/* assets/of-quick-order.js
 * Quick Order Pad DOM controller. Depends on:
 *   - window.OFQuickOrderCore (assets/of-quick-order-core.js)
 *   - window.OF_BADGE_RULES (snippets/of-badge-config.liquid)
 *   - window.__OFNudgesCore (assets/of-nudges-core.js) for normTiers/matches
 * Loaded with `defer` only on the quick-order page (see layout/theme.liquid).
 *
 * GA4: fires of_quick_order_row_resolved, of_quick_order_resolve_failed,
 *      of_quick_order_search_unavailable,
 * of_quick_order_qty_capped, of_quick_order_submit_attempt, and
 * of_quick_order_submit (all params include surface:'pad') via
 * window.OFTrack (assets/of-analytics.js). The "pad viewed" and saved-order
 * events live in of-quick-order-saved.js, which reuses the surface:'pad'
 * convention. See docs/quick-order.md for the full event schema.
 */
(() => {
  'use strict';
  const root = document.querySelector('[data-qop-root]');
  if (!root || root.dataset.qopInit === '1') return;
  root.dataset.qopInit = '1';

  const Core = window.OFQuickOrderCore;
  if (!Core) { console.error('[QOP] core module missing'); return; }

  const TOKEN = root.dataset.storefrontToken || '';
  const DOMAIN = root.dataset.shopDomain || window.location.host;
  var LOW_STOCK = parseInt((root.dataset && root.dataset.lowStockThreshold) || '1', 10);
  if (isNaN(LOW_STOCK)) LOW_STOCK = 1;
  // Contiguous-US gate for the row "Free Shipping" label, computed once in
  // Liquid (snippets/of-free-ship-visible.liquid) and hoisted onto the root,
  // same pattern as snippets/of-card-badge.liquid's fs_visible param.
  const FS_VISIBLE = root.dataset.fsVisible === 'true';
  const RULES = Array.isArray(window.OF_BADGE_RULES) ? window.OF_BADGE_RULES : [];
  const NudgesCore = window.__OFNudgesCore || null;

  // S3 (security fix round, 2026-07-26): of_badge rule fields (label,
  // cart_label, bg_color, text_color) come from a merchant metaobject, but a
  // nightly BOM/optimizer pipeline (docs/quick-order.md) also touches
  // merchant data, and "merchant-controlled" is not a safety guarantee for an
  // HTML sink. Core.escapeHtml() is the ONE audited escaper every label
  // routes through before it lands in an innerHTML template string below.
  // Core.sanitizeColor() allowlists only well-formed hex colors (3/4/6/8
  // digit) before a value reaches a CSSOM color/custom-property, falling back
  // to the given default for anything else. Both are pure and tested in
  // quick-order/tests/core.test.mjs.
  const escapeHtml = Core.escapeHtml;
  const sanitizeColor = Core.sanitizeColor;

  // Build a slug -> { tiers:[{qty,pct}], label } map for the pure totals fn.
  const RULE_MAP = {};
  RULES.forEach((r) => {
    const tiers = NudgesCore ? NudgesCore.normTiers(r.tiers) : (r.tiers || []).map((t) => ({
      qty: parseInt(String(t.min_qty != null ? t.min_qty : (t.qty || 0)).replace(/\D/g, ''), 10) || 0,
      pct: parseInt(String(t.percent || 0).replace(/\D/g, ''), 10) || 0,
    })).filter((t) => t.qty > 0 && t.pct > 0);
    RULE_MAP[r.slug] = {
      tiers, label: r.label || r.slug, any: r.any || [], all: r.all || [],
      cartLabel: (r.cart_label && String(r.cart_label).trim()) || r.label || r.slug,
      bg: sanitizeColor(r.bg_color, '#F3F4F6'), // of-keep: bulk chip
      fg: sanitizeColor(r.text_color, '#1f2937'), // of-keep: bulk chip
    };
  });

  const fmt = (cents) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format((cents || 0) / 100);
  const $ = (s, r = root) => r.querySelector(s);
  const $$ = (s, r = root) => Array.from(r.querySelectorAll(s));

  const rowsHost = $('[data-qop-rows]');
  const rowTmpl = $('[data-qop-row-template]');
  const pickerTmpl = $('[data-qop-picker-template]');

  // STATE: lines = resolved order lines used by the math. Row DOM mirrors it.
  // Each line: { variantId, sku, title, image, priceCents, qty, ruleSlug, ruleLabel, onSale, rowEl }
  let lines = [];
  // Per-rule quantities already in the cart (resolved async). Folds into the
  // discount pool so the pad reflects cart + pad, like the per-PDP Quick Order.
  let cartPool = {};

  // S2: while non-null, saveDraft() stamps the local draft with THIS value
  // instead of Date.now(), and recompute() suppresses the qop:changed signal
  // (which otherwise schedules an account autosave in of-quick-order-saved.js).
  // Set whenever the pad is showing a read-only MIRROR of already-authoritative
  // content (the local draft's own boot restore, or the account `current`
  // envelope winning boot-time arbitration) rather than a genuine edit, so
  // simply loading the page can never manufacture artificial freshness or
  // silently write an unrelated snapshot back to the account. Cleared to null
  // by every genuine-edit entry point below, and by loadPad() itself for its
  // two genuine-action call sites (Start new / Open a saved order).
  let _pinnedStamp = null;

  // S6: which saved order (if any) is open, mirrored from
  // of-quick-order-saved.js via setActiveIdentity() and persisted into the
  // draft so a later boot-time arbitration that picks the DRAFT's content
  // never re-pairs it with a stale/foreign saved-order id.
  let _activeId = null;
  let _activeName = null;

  // R1: bumped by loadPad() on every call. resolveLoadedLines() captures the
  // current value at call time and re-checks it once its fetch resolves, so
  // a slow response from an order opened earlier can never land after (and
  // clobber) a more-recently-opened order's rows.
  let _loadGen = 0;

  // Resolve a rule for a product given its tags (downcased array) + type + vendor.
  function resolveRuleForTags(tagsDown) {
    if (!NudgesCore) return { slug: '', label: '' };
    for (const r of RULES) {
      if (NudgesCore.matches(tagsDown, r) && RULE_MAP[r.slug] && RULE_MAP[r.slug].tiers.length) {
        return { slug: r.slug, label: r.label || r.slug };
      }
    }
    return { slug: '', label: '' };
  }

  // ---- Empty-token guard (warn once, then silently return no data) ----
  let _tokenWarnedOnce = false;

  // ---- Storefront API resolver ----
  async function storefrontQuery(graphql, variables, signal) {
    if (!TOKEN) {
      if (!_tokenWarnedOnce) {
        console.warn('[QOP] Storefront access token is not configured. Searches will return no results.');
        _tokenWarnedOnce = true;
      }
      return null;
    }
    const res = await fetch(`https://${DOMAIN}/api/2024-10/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': TOKEN,
      },
      body: JSON.stringify({ query: graphql, variables }),
      signal: signal || undefined,
    });
    if (!res.ok) throw new Error('storefront ' + res.status);
    const json = await res.json();
    if (json.errors && json.errors.length) {
      console.warn('[QOP] GraphQL errors', json.errors);
    }
    return json.data;
  }

  const PRODUCT_FIELDS = `
    id title handle tags productType vendor
    featuredImage { url(transform: { maxWidth: 200, maxHeight: 200 }) }
    featuredImageLarge: featuredImage { url(transform: { maxWidth: 1200, maxHeight: 1200 }) }
    variants(first: 100) {
      nodes {
        id sku title availableForSale quantityAvailable
        price { amount }
        image { url(transform: { maxWidth: 200, maxHeight: 200 }) }
        imageLarge: image { url(transform: { maxWidth: 1200, maxHeight: 1200 }) }
        selectedOptions { name value }
      }
      pageInfo { hasNextPage endCursor }
    }`;

  // Shared by toResult()'s initial batch and fetchAllVariantsForSku()'s
  // late-page fetch (R4) so both ever build the exact same variant shape.
  function mapVariantNode(v, fallbackImage) {
    return {
      gid: v.id,
      numericId: parseInt(String(v.id).replace(/^.*\//, ''), 10),
      sku: v.sku || '',
      title: v.title,
      available: !!v.availableForSale,
      qty: (typeof v.quantityAvailable === 'number' ? v.quantityAvailable : null),
      priceCents: Math.round(parseFloat((v.price && v.price.amount) || '0') * 100),
      image: (v.image && v.image.url) || fallbackImage || '',
      imageLarge: (v.imageLarge && v.imageLarge.url) || (v.image && v.image.url) || fallbackImage || '',
    };
  }

  // R4: variants(first:100) truncates products with more than 100 variants.
  // Shopify's full-text SKU search still finds the PRODUCT regardless (the
  // index isn't limited by what fields we selected), but the matching
  // variant itself can be invisible to us if it's past the 100th. Page
  // through the rest (bounded to 5 extra pages = 500 more variants) looking
  // for an exact canonical SKU match before giving up on it. Only ever
  // called when the first page reported pageInfo.hasNextPage, so the common
  // (small product) search path never pays this extra cost.
  async function fetchAllVariantsForSku(productId, targetSkuCanonical, signal) {
    let cursor = null;
    for (let page = 0; page < 5; page++) {
      const q = `query($id:ID!,$after:String){ node(id:$id){ ... on Product { variants(first:100, after:$after){ nodes { id sku title availableForSale quantityAvailable price { amount } image { url(transform:{maxWidth:200,maxHeight:200}) } imageLarge: image { url(transform:{maxWidth:1200,maxHeight:1200}) } } pageInfo { hasNextPage endCursor } } } } }`;
      let data;
      try {
        data = await storefrontQuery(q, { id: productId, after: cursor }, signal);
      } catch (e) {
        if (e && e.name === 'AbortError') throw e;
        return null; // best-effort only; leave the caller's result as-is
      }
      const conn = data && data.node && data.node.variants;
      if (!conn) return null;
      const found = (conn.nodes || []).find((v) => Core.canonical(v.sku) === targetSkuCanonical);
      if (found) return found;
      if (!conn.pageInfo || !conn.pageInfo.hasNextPage) return null;
      cursor = conn.pageInfo.endCursor;
    }
    return null;
  }

  // Resolve typed input to ranked product results. The Storefront API does NOT
  // support a `sku:` query field (that is Admin-API-only), passing it makes
  // Shopify silently drop the filter and return the default catalog. Shopify's
  // full-text index DOES cover SKU, so we query the bare part number/name and
  // let Core.rankResults push the exact-SKU match to the top. We try each
  // candidate form (normalized, then hyphen/space-stripped) so "14-051" also
  // resolves "14051". Accepts an AbortSignal so stale requests can be cancelled.
  async function resolveInput(rawInput, signal) {
    const forms = Core.partNumberVariants(rawInput);
    const wantedCanonical = Core.canonical(rawInput);
    const seen = new Set();
    let collected = [];
    let anyNetworkError = false;

    for (const form of forms) {
      const q = `query($q:String!){ products(first:10, query:$q){ nodes { ${PRODUCT_FIELDS} } } }`;
      let data;
      try { data = await storefrontQuery(q, { q: form }, signal); } catch (e) {
        if (e && e.name === 'AbortError') throw e; // propagate abort so caller can ignore
        anyNetworkError = true; // C7: a fetch/HTTP failure is not "no match"
        data = null;
      }
      const nodes = (data && data.products && data.products.nodes) || [];
      for (const p of nodes) if (!seen.has(p.id)) { seen.add(p.id); collected.push(toResult(p)); }
      // R5: don't stop at the first form with ANY hits, keep trying
      // subsequent forms (e.g. the hyphen-stripped one) until an EXACT
      // canonical SKU match turns up, so a fuzzy hit on the first form never
      // shadows an exact hit only reachable via a later form. Still
      // short-circuits once we have an exact match, so the common
      // single-hit case stays fast (still at most len(forms) requests).
      const hasExact = wantedCanonical && collected.some((r) => r.variants.some((v) => Core.canonical(v.sku) === wantedCanonical));
      if (hasExact) break;
    }

    // R4: a SKU-shaped query matched a product but not among its first 100
    // variants, page through the rest before declaring it absent.
    if (wantedCanonical) {
      for (const r of collected) {
        if (!r._hasMoreVariants) continue;
        if (r.variants.some((v) => Core.canonical(v.sku) === wantedCanonical)) continue;
        try {
          const extra = await fetchAllVariantsForSku(r.id, wantedCanonical, signal);
          if (extra) {
            r.variants.push(mapVariantNode(extra, r.image));
            r.variantCount = r.variants.length;
          }
        } catch (e) {
          if (e && e.name === 'AbortError') throw e;
          // best-effort only; leave the result as-is on failure
        }
      }
    }

    const ranked = Core.rankResults(rawInput, collected);
    // C7: if we got real results despite a partial-form failure, they're
    // trustworthy, only surface "network" when EVERY form failed and we
    // truly have nothing to show, so a transient blip doesn't masquerade as
    // "no such part."
    if (!ranked.length && anyNetworkError) {
      const err = new Error('[QOP] resolve network error');
      err.isNetworkError = true;
      throw err;
    }
    return ranked;
  }

  // Flatten a Storefront product node to the result shape the ranker + UI use.
  function toResult(p) {
    const variantConn = p.variants || {};
    const variants = variantConn.nodes || [];
    // Raw product tags only (lowercased), NOT the productType/vendor pseudo-
    // tags mixed in below for rule matching -- the hazardous/free-ship flags
    // must mirror snippets/of-cart-item-messages.liquid's exact tag checks.
    const rawTagsDown = (p.tags || []).map((t) => String(t).toLowerCase());
    const hazardous = rawTagsDown.includes('hazardous item');
    const freeShip = rawTagsDown.includes('free shipping specials');
    const tagsDown = rawTagsDown.slice();
    if (p.productType) tagsDown.push(String(p.productType).toLowerCase());
    if (p.vendor) tagsDown.push(String(p.vendor).toLowerCase());
    const rule = resolveRuleForTags(tagsDown);
    const image = (p.featuredImage && p.featuredImage.url) || '';
    return {
      id: p.id,
      productId: parseInt(String(p.id).replace(/^.*\//, ''), 10),
      handle: p.handle || '',
      title: p.title,
      sku: variants.length ? (variants[0].sku || '') : '',
      variantCount: variants.length,
      image,
      variants: variants.map((v) => mapVariantNode(v, image)),
      rule,
      hazardous,
      freeShip,
      // R4: true when this product has MORE variants than the 100 we fetched.
      _hasMoreVariants: !!(variantConn.pageInfo && variantConn.pageInfo.hasNextPage),
    };
  }

  // ---- Row management ----

  // Monotonically increasing counter for unique result-listbox IDs per row.
  let _rowIdSeq = 0;

  function makeRow() {
    const frag = rowTmpl.content.cloneNode(true);
    const rowEl = frag.querySelector('[data-qop-row]');
    rowsHost.appendChild(frag);
    wireRowInput(rowEl);
    return rowEl;
  }

  function ensureTrailingEmptyRow() {
    const allRows = $$('[data-qop-row]', rowsHost);
    const last = allRows[allRows.length - 1];
    const lastResolved = last && last.querySelector('[data-qop-variant-id]').value;
    if (!last || lastResolved) makeRow();
    renumber();
  }

  function renumber() {
    $$('[data-qop-row]', rowsHost).forEach((r, i) => {
      const n = r.querySelector('[data-qop-line-num]');
      if (n) n.textContent = String(i + 1);
    });
  }

  // Remove a row and its line entirely. Shared by the X button, the minus
  // stepper stepping at/below 0, and typing 0 in the qty field.
  function removeRow(rowEl) {
    _pinnedStamp = null; // S2: genuine edit
    lines = lines.filter((l) => l.rowEl !== rowEl);
    rowEl.remove();
    recompute();
    ensureTrailingEmptyRow();
  }

  // GA4: a typed part number / search term successfully matched a variant
  // (new line or merged into an existing one). rows_resolved is the running
  // count of distinct resolved lines on the pad after this resolve.
  function trackRowResolved() {
    if (window.OFTrack) OFTrack.event('of_quick_order_row_resolved', { surface: 'pad', rows_resolved: lines.length });
  }

  // GA4: the shopper asked for more of a line than inventory allows (stepper
  // or typed qty), so the qty was clamped down to line.maxQty. Only fires
  // when maxQty is a real (non-default) cap and the ask actually exceeded it.
  function trackQtyCapped(line, requestedQty) {
    if (!window.OFTrack) return;
    if (typeof line.maxQty !== 'number') return;
    if (requestedQty > line.maxQty) {
      OFTrack.event('of_quick_order_qty_capped', {
        surface: 'pad', sku: line.sku || '', requested_qty: requestedQty, capped_qty: line.maxQty,
      });
    }
  }

  // C2: the ONE place a line's qty is ever set. Clamps to line.maxQty (999
  // while inventory hasn't resolved yet) and fires of_quick_order_qty_capped
  // via trackQtyCapped (which itself only fires when the request actually
  // exceeded the cap), used by the qty stepper, the typed qty input, AND
  // both duplicate-resolve merge paths (placeVariantInRow, addVariantLine),
  // which previously bypassed the cap entirely. Returns the clamped qty;
  // callers still decide what 0-or-below means (removeRow, or just "no room").
  function setLineQty(line, requestedQty) {
    trackQtyCapped(line, requestedQty);
    return Core.clampLineQty(requestedQty, line.maxQty);
  }

  // Place a resolved single variant into a row (or merge if duplicate).
  function placeVariantInRow(rowEl, result, variant) {
    _pinnedStamp = null; // S2: genuine edit
    // Duplicate? merge qty into the existing line (capped, C2) and clear this
    // empty row's input.
    const existing = lines.find((l) => l.variantId === variant.numericId);
    if (existing) {
      existing.qty = setLineQty(existing, existing.qty + 1);
      syncRowFromLine(existing);
      clearRowInput(rowEl);
      recompute();
      ensureTrailingEmptyRow();
      trackRowResolved();
      return;
    }
    const line = {
      variantId: variant.numericId,
      productId: result.productId,
      handle: result.handle,
      sku: variant.sku || result.sku,
      title: result.title + (result.variantCount > 1 ? ' / ' + variant.title : ''),
      image: variant.image,
      imageLarge: variant.imageLarge || variant.image,
      priceCents: variant.priceCents,
      qty: 1,
      ruleSlug: result.rule.slug,
      ruleLabel: result.rule.label,
      available: variant.available,
      availableCount: variant.qty,
      hazardous: !!result.hazardous,
      freeShip: !!result.freeShip,
      rowEl,
    };
    lines.push(line);
    fillRow(rowEl, line);
    recompute();
    ensureTrailingEmptyRow();
    trackRowResolved();
  }

  // Render (or refresh) the bulk-discount info icon on a row. Idempotent:
  // removes any existing icon first, so it is safe to call again after an
  // async re-resolve restores a loaded line's rule.
  function renderRowDiscountInfo(rowEl, line) {
    const descEl = rowEl.querySelector('[data-qop-desc]');
    if (!descEl) return;
    const prev = descEl.querySelector('.of-qop__group-info');
    if (prev) prev.remove();
    const infoRule = line.ruleSlug ? RULE_MAP[line.ruleSlug] : null;
    if (!(infoRule && infoRule.tiers && infoRule.tiers.length)) return;
    const info = document.createElement('button');
    info.type = 'button';
    info.className = 'of-qop__group-info';
    info.style.color = infoRule.fg || 'currentColor';
    info.dataset.qopDot = line.ruleSlug;
    info.setAttribute('aria-label', (infoRule.cartLabel || '') + ' bulk discount details');
    info.innerHTML = '<svg viewBox="0 0 16 16" width="17" height="17" aria-hidden="true" focusable="false"><circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" stroke-width="1.4"/><circle cx="8" cy="4.6" r="1" fill="currentColor"/><rect x="7.25" y="6.8" width="1.5" height="5" rx="0.75" fill="currentColor"/></svg>';
    info.addEventListener('mouseenter', () => showDotPopover(info, line.ruleSlug));
    info.addEventListener('focus', () => showDotPopover(info, line.ruleSlug));
    info.addEventListener('mouseleave', hideDotPopover);
    info.addEventListener('blur', hideDotPopover);
    descEl.prepend(info);
  }

  // Render the compact color-coded label row (data-qop-labels, lives inside
  // .of-qop__cell--desc under the description/stock text -- see
  // snippets/quick-order-row.liquid). Rebuilds from scratch every call so it
  // stays correct across recompute() re-runs (pooled qty/tier state changes)
  // without any separate diffing. pct/poolQty are the same values recompute()
  // already computed for this line's bulk-discount math, passed in so this
  // function doesn't repeat that work.
  //   bulk     -- moved to the Unit column on 2026-10-26 (renderBulkChip,
  //               below): it sits directly under the price and opens the
  //               product page's tier table over the row.
  //   freeship -- tag-driven, mirrors snippets/of-cart-item-messages.liquid's
  //               'Free Shipping Specials' gate (plus the contiguous-US
  //               FS_VISIBLE gate, mirrors of-card-badge.liquid's fs_visible).
  //   hazard   -- tag-driven, mirrors of-cart-item-messages.liquid's
  //               'Hazardous Item' gate.
  function renderRowLabels(line, pct, poolQty) {
    if (!line.rowEl) return;
    const host = line.rowEl.querySelector('[data-qop-labels]');
    if (!host) return;
    host.innerHTML = '';
    const chips = [];

    renderBulkChip(line, pct, poolQty);

    if (FS_VISIBLE && line.freeShip) {
      const chip = document.createElement('span');
      chip.className = 'of-qop__label of-qop__label--freeship';
      chip.textContent = 'Free Shipping';
      chips.push(chip);
    }

    if (line.hazardous) {
      const chip = document.createElement('span');
      chip.className = 'of-qop__label of-qop__label--hazard';
      const icon = document.createElement('img');
      icon.src = 'https://cdn.shopify.com/s/files/1/0609/9832/0342/files/hazardous-icon.svg';
      icon.width = 9; icon.height = 9; icon.alt = ''; icon.loading = 'lazy';
      chip.appendChild(icon);
      chip.appendChild(document.createTextNode('Hazardous'));
      chips.push(chip);
    }

    chips.forEach((c) => host.appendChild(c));
    host.hidden = chips.length === 0;
  }

  // The bulk savings chip, in the Unit column directly under the price
  // (Rhett, 2026-10-26: "move it into the PRICE column directly under the
  // price; clicking opens the product page's tier table as an overlay").
  // Same words and rule colours as before (Core.bulkLabel, RULE_MAP). The
  // chip is a button: assets/of-tier-pop.js opens the shared tier table
  // (of-nudges-core.js tierTableHtml) at the pad's pooled quantity for the
  // rule (cart plus pad, Quick Order's pooling), so the current row is the
  // break this line is priced at.
  function renderBulkChip(line, pct, poolQty) {
    const chip = line.rowEl && line.rowEl.querySelector('[data-qop-bulk]');
    if (!chip) return;
    const rm = line.ruleSlug ? (RULE_MAP[line.ruleSlug] || null) : null;
    let txt = '';
    let pooled = line.qty;
    if (rm && rm.tiers && rm.tiers.length) {
      pooled = (line.ruleSlug in poolQty) ? poolQty[line.ruleSlug] : line.qty;
      const step = (NudgesCore && NudgesCore.nextTier) ? NudgesCore.nextTier(pooled, rm.tiers || []) : { next: null };
      txt = Core.bulkLabel(pct, pooled, step && step.next);
    }
    chip.hidden = !txt;
    chip.textContent = txt;
    if (!txt) { chip.removeAttribute('aria-label'); return; }
    chip.style.setProperty('--of-qop-disc-bg', rm.bg || '#F3F4F6'); // of-keep: bulk chip
    chip.style.setProperty('--of-qop-disc-fg', rm.fg || '#1f2937'); // of-keep: bulk chip
    chip.setAttribute('aria-label', txt + '. Bulk pricing for ' + (line.title || line.sku || 'this item'));
    chip.dataset.qopPooled = String(pooled);
    // A table left open would show the old quantity: it closes (a press on
    // the stepper or anywhere else closes it anyway).
    const Pop = window.OFTierPop;
    if (Pop && Pop.isOpenFor(chip)) Pop.close();
  }

  function openBulk(chip, line) {
    const Pop = window.OFTierPop;
    const rm = line && line.ruleSlug ? RULE_MAP[line.ruleSlug] : null;
    if (!Pop || !rm || !rm.tiers || !rm.tiers.length) return;
    Pop.open(chip, {
      title: line.title || line.sku || '',
      label: rm.cartLabel || rm.label || '',
      tiers: rm.tiers,
      priceCents: line.priceCents,
      qty: Number(chip.dataset.qopPooled) || line.qty
    });
  }

  // Apply inventory state to a line + its row: set maxQty cap and render the
  // on-hand stock label. line.availableCount is quantityAvailable (number|null).
  function applyInventoryToRow(line) {
    if (!line || !line.rowEl) return;
    var st = Core.inventoryState(line.available !== false, (typeof line.availableCount === 'number' ? line.availableCount : null), LOW_STOCK);
    line.maxQty = st.maxQty;
    var el = line.rowEl.querySelector('[data-qop-stock]');
    if (el) {
      el.textContent = st.label;
      el.className = 'of-qop__stock of-qop__stock--' + st.level;
      el.hidden = false;
    }
    // Clamp current qty to the new cap.
    if (line.qty > st.maxQty) { line.qty = st.maxQty; var q = line.rowEl.querySelector('[data-qop-qty]'); if (q) q.value = String(line.qty); }
  }

  function fillRow(rowEl, line) {
    // Mark the row resolved so its qty stepper + remove button become visible
    // (both are hidden on the blank/in-progress row, see of-quick-order.css).
    rowEl.classList.add('is-resolved');
    const input = rowEl.querySelector('[data-qop-part-input]');
    input.value = line.sku || line.title;
    input.readOnly = true; // resolved lines lock the part field, remove the line to change it
    rowEl.querySelector('[data-qop-variant-id]').value = String(line.variantId);
    rowEl.querySelector('[data-qop-price-cents]').value = String(line.priceCents);
    rowEl.querySelector('[data-qop-rule-slug]').value = line.ruleSlug || '';
    rowEl.querySelector('[data-qop-rule-label]').value = line.ruleLabel || '';
    const descEl = rowEl.querySelector('[data-qop-desc]');
    descEl.textContent = line.title;
    // Group info icon on discounted lines: identifies the discount group and,
    // on hover/focus, opens a popover with the tier ladder + live progress
    // (so a shopper sees the deal even before reaching the first tier).
    renderRowDiscountInfo(rowEl, line);
    const thumbBtn = rowEl.querySelector('[data-qop-thumb-btn]');
    const thumb = rowEl.querySelector('[data-qop-thumb]');
    if (line.image) {
      thumb.src = line.image; thumbBtn.hidden = false;
      thumbBtn.dataset.imgFull = line.imageLarge || line.image;
      thumbBtn.dataset.sku = line.sku || '';
    }
    // "View product" link: opens /products/<handle> in a new tab. Legacy
    // saved lines captured before handle-tracking was added have no handle
    // yet (resolveLoadedLines() fills it in once the live lookup returns);
    // stay hidden rather than link to a dead/wrong URL until then.
    const viewLink = rowEl.querySelector('[data-qop-view-link]');
    if (viewLink) {
      if (line.handle) {
        viewLink.href = '/products/' + line.handle;
        viewLink.dataset.sku = line.sku || '';
        viewLink.hidden = false;
      } else {
        viewLink.hidden = true;
        viewLink.removeAttribute('href');
      }
    }
    rowEl.querySelector('[data-qop-qty]').value = String(line.qty);
    applyInventoryToRow(line);
    const results = rowEl.querySelector('[data-qop-results]');
    if (results) {
      results.hidden = true;
      results.innerHTML = '';
      // Close the listbox: update aria-expanded on the paired input.
      input.setAttribute('aria-expanded', 'false');
    }
  }

  function syncRowFromLine(line) {
    if (line.rowEl) line.rowEl.querySelector('[data-qop-qty]').value = String(line.qty);
  }

  function clearRowInput(rowEl) {
    // Back to the in-progress state: hide qty stepper + remove button again.
    rowEl.classList.remove('is-resolved');
    rowEl.querySelector('[data-qop-variant-id]').value = '';
    const qty = rowEl.querySelector('[data-qop-qty]');
    if (qty) qty.value = '0';
    const input = rowEl.querySelector('[data-qop-part-input]');
    input.value = '';
    input.readOnly = false; // editable again once the line is cleared
    input.setAttribute('aria-expanded', 'false');
    const results = rowEl.querySelector('[data-qop-results]');
    if (results) { results.hidden = true; results.innerHTML = ''; }
  }

  // Render the results dropdown for a row.
  // Text is inserted via textContent to prevent XSS from catalog data.
  // `query` (the raw typed term) is optional and used only for the GA4
  // resolve-failed event below. `opts.networkError` (C7): a fetch/HTTP
  // failure, not a genuine catalog miss, different copy, no GA4 event
  // (of_quick_order_resolve_failed specifically means "no such part").
  function renderResults(rowEl, ranked, query, opts) {
    opts = opts || {};
    const box = rowEl.querySelector('[data-qop-results]');
    const noMatch = rowEl.querySelector('[data-qop-nomatch]');
    const input = rowEl.querySelector('[data-qop-part-input]');
    box.innerHTML = '';
    if (!ranked.length) {
      box.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      if (opts.networkError) {
        noMatch.textContent = 'Search unavailable, retry';
        noMatch.hidden = false;
        /* The resolver itself failed, which is NOT a catalog miss, so this is
         * its own event and deliberately not of_quick_order_resolve_failed.
         *
         * This is the event the 2026-07-27 incident would have fired. That was
         * a redirect on the endpoint the pad calls: every form failed, so
         * resolveInput threw isNetworkError, so the pad rendered "Search
         * unavailable, retry" and told nobody. It ran silent for days. The
         * Insights Needs Attention queue spike-detects these separately from
         * catalog misses, because "the resolver is down" and "customers are
         * typing part numbers we do not carry" are different problems with
         * different fixes.
         *
         * NO QUERY PARAM, on purpose. The failure is about the resolver, not
         * about the term, so the term would be noise, and a param that is
         * never sent is a param that can never leak: there is nothing here to
         * redact.
         */
        if (window.OFTrack) {
          OFTrack.event('of_quick_order_search_unavailable', { surface: 'pad' });
        }
        return;
      }
      noMatch.textContent = 'No match';
      noMatch.hidden = false;
      // GA4: the typed term did not resolve to any product. `query` is the
      // shopper's own typed part number / search text, usually catalog
      // input but not guaranteed (S7, security fix round 2026-07-26: a
      // customer can type anything in that box, including their own email
      // address). Redact anything email-shaped BEFORE truncating, so a long
      // email can never survive partially past the 40-char cut.
      if (window.OFTrack && query) {
        const safeQuery = Core.redactEmailLike(String(query)).slice(0, 40);
        OFTrack.event('of_quick_order_resolve_failed', { surface: 'pad', query: safeQuery });
      }
      return;
    }
    noMatch.hidden = true;
    ranked.forEach((res) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'of-qop__result';
      btn.setAttribute('role', 'option');

      const skuSpan = document.createElement('span');
      skuSpan.className = 'of-qop__result-sku';
      skuSpan.textContent = res.sku || '';

      const titleSpan = document.createElement('span');
      titleSpan.className = 'of-qop__result-title';
      titleSpan.textContent = res.title;

      btn.appendChild(skuSpan);
      btn.appendChild(titleSpan);

      if (res.variantCount > 1) {
        const multiSpan = document.createElement('span');
        multiSpan.className = 'of-qop__result-multi';
        multiSpan.textContent = res.variantCount + ' options';
        btn.appendChild(multiSpan);
      }

      btn.addEventListener('click', () => {
        box.hidden = true;
        input.setAttribute('aria-expanded', 'false');
        if (res.variantCount > 1) openPicker(rowEl, res);
        else if (res.variants[0]) placeVariantInRow(rowEl, res, res.variants[0]);
      });
      box.appendChild(btn);
    });
    box.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  }

  const debounce = NudgesCore ? NudgesCore.debounce : (fn, d) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), d); }; };

  function wireRowInput(rowEl) {
    const input = rowEl.querySelector('[data-qop-part-input]');
    const results = rowEl.querySelector('[data-qop-results]');

    // Stamp a unique id on this row's result listbox and wire ARIA relationship.
    const listboxId = 'of-qop-results-' + (++_rowIdSeq);
    results.id = listboxId;
    input.setAttribute('aria-controls', listboxId);
    // input already ships with aria-expanded="false" in markup; we manage it at runtime.

    // Per-row AbortController so we can cancel a stale in-flight request.
    let _pendingAbort = null;

    const onType = debounce(async () => {
      const val = input.value.trim();
      if (val.length < 2) {
        results.hidden = true;
        input.setAttribute('aria-expanded', 'false');
        // R3: clearing/shortening the input below 2 chars aborts the active
        // request instead of leaving it to resolve into a now-irrelevant row.
        if (_pendingAbort) { _pendingAbort.abort(); _pendingAbort = null; }
        return;
      }
      // Abort the previous in-flight request if still pending.
      if (_pendingAbort) { _pendingAbort.abort(); }
      // R2: capture THIS request's own controller locally. Only ever clear
      // _pendingAbort if it still === myAbort, an older request's abort/
      // success/failure handling must never null out a newer request's
      // controller (it would orphan it, so a later abort attempt on it silently
      // no-ops instead of actually cancelling).
      const myAbort = new AbortController();
      _pendingAbort = myAbort;
      const signal = myAbort.signal;
      try {
        const ranked = await resolveInput(val, signal);
        if (_pendingAbort === myAbort) _pendingAbort = null;
        renderResults(rowEl, ranked, val);
      } catch (e) {
        if (e && e.name === 'AbortError') { if (_pendingAbort === myAbort) _pendingAbort = null; return; } // stale request aborted; ignore silently
        if (_pendingAbort === myAbort) _pendingAbort = null;
        console.error('[QOP] resolve failed', e);
        // C7: a network/HTTP failure is not "no such part", say so
        // distinctly and don't count it as a catalog miss in GA4.
        renderResults(rowEl, [], val, { networkError: !!(e && e.isNetworkError) });
      }
    }, 220);

    input.addEventListener('input', onType);

    // Close the dropdown when focus leaves the part cell.
    input.addEventListener('blur', () => {
      // Small delay so a click on a result button fires before we hide.
      setTimeout(() => {
        if (!rowEl.contains(document.activeElement)) {
          results.hidden = true;
          input.setAttribute('aria-expanded', 'false');
        }
      }, 150);
    });

    // qty stepper + manual qty. Stepping to 0 (or below) removes the line, as
    // does typing 0, both routed through removeRow so the X button, the minus
    // button, and a typed 0 all behave identically.
    rowEl.querySelectorAll('[data-qop-step]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const line = lines.find((l) => l.rowEl === rowEl);
        if (!line) return;
        const step = parseInt(btn.dataset.qopStep, 10) || 0;
        const requested = line.qty + step;
        const next = setLineQty(line, requested); // C2
        if (next <= 0) { removeRow(rowEl); return; }
        _pinnedStamp = null; // S2: genuine edit
        line.qty = next;
        syncRowFromLine(line);
        recompute();
      });
    });
    rowEl.querySelector('[data-qop-qty]').addEventListener('input', (e) => {
      const line = lines.find((l) => l.rowEl === rowEl);
      if (!line) return;
      // Empty field while editing: leave the line alone until they commit a value.
      if (e.target.value === '') return;
      const requested = parseInt(e.target.value, 10) || 0;
      if (requested <= 0) { removeRow(rowEl); return; }
      _pinnedStamp = null; // S2: genuine edit
      const capped = setLineQty(line, requested); // C2
      line.qty = Math.max(1, capped);
      recompute();
    });
    rowEl.querySelector('[data-qop-row-remove]').addEventListener('click', () => removeRow(rowEl));
    const bulkChip = rowEl.querySelector('[data-qop-bulk]');
    if (bulkChip) bulkChip.addEventListener('click', () => {
      const line = lines.find((l) => l.rowEl === rowEl);
      if (line) openBulk(bulkChip, line);
    });
  }

  // ---- Totals render (uses pure orderTotals; mirrors of_badge math) ----
  const subtotalEl = $('[data-qop-subtotal]');
  const totalEl = $('[data-qop-total]');
  const discountsHost = $('[data-qop-discounts]');
  const addAllBtn = $('[data-qop-add-all]');
  const addAllTotal = $('[data-qop-add-all-total]');

  function recompute() {
    const totals = Core.orderTotals(lines, RULE_MAP, cartPool);
    // Per-line unit + amount with the active pooled discount for that line's rule.
    const poolPct = {};
    const poolQty = {};
    totals.discounts.forEach((d) => { poolPct[d.slug] = d.pct; poolQty[d.slug] = d.pooledQty; });
    (totals.opportunities || []).forEach((o) => { poolQty[o.slug] = o.pooledQty; });
    lines.forEach((line) => {
      const pct = (line.ruleSlug && poolPct[line.ruleSlug]) || 0;
      const lt = Core.lineTotals({ priceCents: line.priceCents, qty: line.qty }, pct);
      const rowEl = line.rowEl;
      // FIX 3: draft-restored lines whose rowEl was never appended would throw below.
      if (!rowEl) return;
      const unitBase = rowEl.querySelector('[data-qop-unit-base]');
      const unitDisc = rowEl.querySelector('[data-qop-unit-disc]');
      const amount = rowEl.querySelector('[data-qop-amount]');
      if (lt.discounted) {
        unitBase.textContent = fmt(line.priceCents); unitBase.classList.add('is-struck');
        unitDisc.textContent = fmt(lt.unitCents); unitDisc.hidden = false;
        amount.classList.add('is-sale');
      } else {
        unitBase.textContent = fmt(line.priceCents); unitBase.classList.remove('is-struck');
        unitDisc.hidden = true; unitDisc.textContent = '';
        amount.classList.remove('is-sale');
      }
      amount.textContent = fmt(lt.lineCents);
      // Color-coded label row: bulk savings / free shipping / hazardous.
      renderRowLabels(line, pct, poolQty);
    });

    subtotalEl.textContent = fmt(totals.subtotalCents);
    totalEl.textContent = fmt(totals.totalCents);

    // Footer Discounts list: one color-coded row per rule group, in the
    // collection-card badge style, naming = cart_label + current % + next-tier.
    // Labels come from a merchant metaobject, but S3 (security fix round,
    // 2026-07-26): a nightly BOM/optimizer pipeline also touches merchant
    // data, so the label still routes through escapeHtml() before
    // concatenation. Colors are pre-sanitized into RULE_MAP above.
    discountsHost.innerHTML = '';
    totals.discounts.forEach((d) => {
      const rm = RULE_MAP[d.slug] || {};
      const row = document.createElement('div');
      row.className = 'of-qop__discount';
      row.style.setProperty('--of-qop-disc-bg', rm.bg || '#F3F4F6'); // of-keep: bulk chip
      row.style.setProperty('--of-qop-disc-fg', rm.fg || '#1f2937'); // of-keep: bulk chip
      let nudge = '';
      const step = (NudgesCore && NudgesCore.nextTier) ? NudgesCore.nextTier(d.pooledQty, rm.tiers || []) : { next: null };
      if (step && step.next) {
        const need = step.next.qty - d.pooledQty;
        if (need > 0) nudge = ' &middot; <span class="of-qop__discount-nudge">' + need + ' more for ' + step.next.pct + '%</span>';
      }
      const name = escapeHtml(rm.cartLabel || d.label);
      row.innerHTML = '<span>' + name + ' &middot; ' + d.pct + '% off' + nudge + '</span><span>&minus;' + fmt(d.amountCents) + '</span>';
      discountsHost.appendChild(row);
    });
    // Opportunity rows: groups on the pad below their first tier, an always-on
    // "add N more to start saving" nudge (dashed accent, no amount yet).
    (totals.opportunities || []).forEach((o) => {
      const rm = RULE_MAP[o.slug] || {};
      const step = (NudgesCore && NudgesCore.nextTier) ? NudgesCore.nextTier(o.pooledQty, rm.tiers || []) : { next: null };
      if (!step || !step.next) return;
      const need = step.next.qty - o.pooledQty;
      if (need <= 0) return;
      const row = document.createElement('div');
      row.className = 'of-qop__discount is-opportunity';
      row.style.setProperty('--of-qop-disc-bg', rm.bg || '#F3F4F6'); // of-keep: text on a fill, dark block gives it ink (plan carry-in)
      row.style.setProperty('--of-qop-disc-fg', rm.fg || '#1f2937'); // of-keep: text on a fill, dark block gives it ink (plan carry-in)
      const name = escapeHtml(rm.cartLabel || o.label);
      row.innerHTML = '<span>' + name + ' &middot; <span class="of-qop__discount-nudge">add ' + need + ' more for ' + step.next.pct + '% off</span></span><span></span>';
      discountsHost.appendChild(row);
    });
    discountsHost.hidden = !discountsHost.children.length;

    const hasQty = totals.totalQty > 0;
    addAllBtn.setAttribute('aria-disabled', hasQty ? 'false' : 'true');
    addAllTotal.textContent = totals.totalQty + (totals.totalQty === 1 ? ' item · ' : ' items · ') + fmt(totals.totalCents);

    saveDraft();
    // S2: a pinned stamp means the pad is currently showing a read-only
    // mirror (boot restore of the local draft, or the account envelope
    // winning arbitration) rather than a genuine edit, suppress the signal
    // so of-quick-order-saved.js's autosave listener doesn't write it
    // straight back to the account. Cleared by every genuine-edit call site.
    if (_pinnedStamp == null) {
      try { document.dispatchEvent(new CustomEvent('qop:changed')); } catch (_) {}
    }
  }

  // ---- Multi-variant inline picker ----
  let openPickerEl = null;
  // Remember what was focused before the picker opened so we can restore it.
  let _pickerTrigger = null;

  // Escape-key handler that closes the picker; stored so it can be removed.
  function _pickerKeydown(e) {
    if (e.key === 'Escape') closePicker();
  }

  function openPicker(rowEl, result) {
    // Remember the currently focused element before we open the dialog.
    _pickerTrigger = document.activeElement || null;
    closePicker();
    // Clone the outer picker template content.
    const frag = pickerTmpl.content.cloneNode(true);
    const picker = frag.querySelector('[data-qop-picker]');
    picker.querySelector('[data-qop-picker-title]').textContent = result.title;
    const rowsBox = picker.querySelector('[data-qop-picker-rows]');

    // Discount nudge for this product's rule (if any) + live discounted Each.
    // Pool = cart(rule) + pad(rule) + the qtys being entered in this picker.
    const rule = (result.rule && result.rule.slug) ? RULE_MAP[result.rule.slug] : null;
    const nudgeEl = document.createElement('div');
    nudgeEl.className = 'of-qop__picker-nudge';
    if (rule) {
      nudgeEl.style.setProperty('--of-qop-disc-fg', rule.fg || '#1f2937'); // of-keep: text on a fill, dark block gives it ink (plan carry-in)
      picker.querySelector('[data-qop-picker-table]').before(nudgeEl);
    }
    function repaintPicker() {
      if (!rule) return;
      const slug = result.rule.slug;
      let pickerQty = 0;
      rowsBox.querySelectorAll('[data-qop-picker-qty]').forEach((q) => { pickerQty += parseInt(q.value, 10) || 0; });
      const pooled = (cartPool[slug] || 0) + padQtyForSlug(slug) + pickerQty;
      const pct = Core.activeTierForQty(pooled, rule.tiers);
      const step = (NudgesCore && NudgesCore.nextTier) ? NudgesCore.nextTier(pooled, rule.tiers) : { next: null };
      let txt = escapeHtml(rule.cartLabel) + ': ' + pct + '% off applied';
      if (step && step.next && (step.next.qty - pooled) > 0) txt += ' &middot; add ' + (step.next.qty - pooled) + ' more for ' + step.next.pct + '%';
      nudgeEl.innerHTML = txt;
      rowsBox.querySelectorAll('[data-qop-picker-row]').forEach((r) => {
        const v = r._variant; const each = r.querySelector('[data-qop-picker-each]');
        if (!each || !v) return;
        if (pct > 0) {
          const disc = Math.round(v.priceCents * (100 - pct) / 100);
          each.innerHTML = '<s class="of-qop__picker-each-base">' + fmt(v.priceCents) + '</s> <b class="of-qop__picker-each-disc">' + fmt(disc) + '</b>';
        } else {
          each.textContent = fmt(v.priceCents);
        }
      });
    }
    // The per-variant row prototype lives in a <template class="of-qop__picker-tmpl">
    // that is a SIBLING of the picker div within the cloned fragment (it sits
    // after the picker div's close in quick-order-variant-picker.liquid), not a
    // descendant, so query `frag`, not `picker`. Mirrors makeRow's
    // clone-then-query-the-fragment pattern. We clone from .content to respect
    // the template element.
    const rowTmplEl = frag.querySelector('.of-qop__picker-tmpl');
    const rowProto = rowTmplEl ? rowTmplEl.content.firstElementChild : null;
    if (!rowProto) {
      console.error('[QOP] picker row template not found');
      return;
    }
    result.variants.forEach((v) => {
      const r = rowProto.cloneNode(true);
      r.querySelector('[data-qop-picker-val]').textContent = v.title;
      const stock = r.querySelector('[data-qop-picker-stock]');
      stock.textContent = v.available ? (v.qty > 0 ? v.qty + ' in stock' : 'In stock') : 'Out of stock';
      if (!v.available) stock.classList.add('is-oos');
      r.querySelector('[data-qop-picker-each]').textContent = fmt(v.priceCents);
      const qtyInput = r.querySelector('[data-qop-picker-qty]');
      qtyInput.dataset.variantId = String(v.numericId);
      // availableForSale is false only when out of stock AND the variant cannot
      // be oversold, exactly "out of stock and not allowed to be sold". Disable
      // its qty input so it can't be added to the order.
      if (!v.available) { qtyInput.disabled = true; qtyInput.title = 'Out of stock'; }
      r._variant = v;
      rowsBox.appendChild(r);
    });
    rowsBox.addEventListener('input', (e) => { if (e.target.matches('[data-qop-picker-qty]')) repaintPicker(); });
    repaintPicker();
    picker.querySelector('[data-qop-picker-close]').addEventListener('click', closePicker);
    picker.querySelector('[data-qop-picker-add]').addEventListener('click', () => {
      _pinnedStamp = null; // S2: genuine edit
      const chosen = Array.from(rowsBox.querySelectorAll('[data-qop-picker-row]'))
        .map((r) => ({ v: r._variant, q: parseInt(r.querySelector('[data-qop-picker-qty]').value, 10) || 0 }))
        .filter((x) => x.q > 0);
      closePicker();
      // The first chosen variant reuses the row the user typed in; the rest get
      // new rows. This avoids leaving the typed row blank in the middle of the
      // list. rowConsumed flips once a pick actually lands in rowEl.
      let rowConsumed = false;
      chosen.forEach((x) => {
        if (addVariantLine(result, x.v, x.q, rowConsumed ? null : rowEl)) rowConsumed = true;
      });
      // Nothing landed in the typed row (no picks, or every pick merged into an
      // existing line elsewhere), drop it so it doesn't linger as a blank row.
      if (!rowConsumed) { lines = lines.filter((l) => l.rowEl !== rowEl); rowEl.remove(); }
      ensureTrailingEmptyRow();
      recompute();
    });
    rowEl.after(picker);
    openPickerEl = picker;
    // Move focus into the dialog for accessibility (aria-modal="true", tabindex="-1").
    picker.focus();
    // Register Escape key handler to close.
    document.addEventListener('keydown', _pickerKeydown);
  }

  function closePicker() {
    if (openPickerEl) {
      openPickerEl.remove();
      openPickerEl = null;
      document.removeEventListener('keydown', _pickerKeydown);
      // Restore focus to the element that was active before the picker opened.
      if (_pickerTrigger && typeof _pickerTrigger.focus === 'function') {
        _pickerTrigger.focus();
      }
      _pickerTrigger = null;
    }
  }

  // Add (or merge) a variant as a line. Reuses reuseRowEl (the row the user
  // typed in) when given, else appends a new row. Returns true if it placed the
  // line into reuseRowEl (so the caller knows that row was consumed), false if
  // it merged into an existing line or appended a fresh row.
  function addVariantLine(result, variant, qty, reuseRowEl) {
    const existing = lines.find((l) => l.variantId === variant.numericId);
    if (existing) { existing.qty = setLineQty(existing, existing.qty + qty); syncRowFromLine(existing); trackRowResolved(); return false; } // C2
    const rowEl = reuseRowEl || makeRow();
    const line = {
      variantId: variant.numericId,
      productId: result.productId,
      handle: result.handle,
      sku: variant.sku || result.sku,
      title: result.title + ' / ' + variant.title,
      image: variant.image,
      imageLarge: variant.imageLarge || variant.image,
      priceCents: variant.priceCents,
      qty: qty,
      ruleSlug: result.rule.slug,
      ruleLabel: result.rule.label,
      available: variant.available,
      availableCount: variant.qty,
      hazardous: !!result.hazardous,
      freeShip: !!result.freeShip,
      rowEl,
    };
    lines.push(line);
    fillRow(rowEl, line);
    trackRowResolved();
    return rowEl === reuseRowEl;
  }

  // Discount codes are entered at checkout (the Storefront token can't validate
  // them, and order-only / non-combinable codes can't be previewed here), so the
  // pad shows no code field, only the auto-applied volume discounts.

  // ---- Add all to cart ----
  const notesInput = $('[data-qop-notes]');
  const cartModal = $('[data-qop-cart-modal]');
  let _cartPlan = null;      // "Replace my cart": absolute target quantities
  let _mergeCartPlan = null; // "Add to my cart": capped against room remaining in the cart (C3)

  // Note-only edits don't touch `lines`, so nothing else calls recompute()
  // for them. Without this, typing a note never reaches saveDraft() or fires
  // qop:changed, so it's invisible to both the localStorage draft and the
  // saved-pad auto-save.
  // S9: saveDraft() runs SYNCHRONOUSLY on every keystroke (sub-400ms
  // navigation must not lose a typed note), only the qop:changed signal
  // (which schedules the ~2.5s debounced account write in
  // of-quick-order-saved.js) stays debounced, matching the cart-pool refresh
  // pattern below.
  if (notesInput) {
    const _dispatchNotesChanged = (NudgesCore && NudgesCore.debounce)
      ? NudgesCore.debounce(() => { try { document.dispatchEvent(new CustomEvent('qop:changed')); } catch (_) {} }, 400)
      : () => { try { document.dispatchEvent(new CustomEvent('qop:changed')); } catch (_) {} };
    notesInput.addEventListener('input', () => {
      _pinnedStamp = null; // S2: genuine edit
      saveDraft();
      _dispatchNotesChanged();
    });
  }

  function _escAttr(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  function _cartLineHtml(img, title, qtyHtml) {
    var imgHtml = img
      ? '<img class="of-qop__cart-thumb" src="' + _escAttr(img) + '" alt="" width="40" height="40" loading="lazy">'
      : '<span class="of-qop__cart-thumb of-qop__cart-thumb--blank"></span>';
    return '<li class="of-qop__cart-line">' + imgHtml +
      '<span class="of-qop__cart-line-title">' + _escAttr(title) + '</span>' +
      '<span class="of-qop__cart-line-qty">' + qtyHtml + '</span></li>';
  }
  function _buildCartModalBody(plan) {
    var html = '';
    if (plan.cartLines.length) {
      html += '<h3 class="of-qop__cart-subhead">Your current cart</h3><ul class="of-qop__cart-list">';
      plan.cartLines.forEach(function (c) {
        var q = c.affected
          ? ('Qty ' + c.quantity + ' &rarr; <strong>' + c.newQty + '</strong>')
          : ('Qty ' + c.quantity);
        html += _cartLineHtml(c.image, c.title, q);
      });
      html += '</ul>';
    }
    var newItems = plan.addable.filter(function (a) { return a.isNew; });
    if (newItems.length) {
      html += '<h3 class="of-qop__cart-subhead">Will be added</h3><ul class="of-qop__cart-list">';
      newItems.forEach(function (a) { html += _cartLineHtml(a.image, a.title, 'Qty ' + a.qty); });
      html += '</ul>';
    }
    return html;
  }
  // C4: the modal owns re-enabling the trigger when the shopper backs out
  // without submitting (X button, overlay click, Escape all route here).
  function closeCartModal() { if (cartModal) cartModal.hidden = true; addAllBtn.setAttribute('aria-disabled', 'false'); }
  function openAddAllModal(plan, mergePlan) {
    if (!cartModal) { submitToCart('merge', mergePlan || plan); return; } // markup missing -> safe fallback
    _cartPlan = plan;
    _mergeCartPlan = mergePlan || plan;
    var titleEl = cartModal.querySelector('[data-qop-cart-title]');
    var bodyEl = cartModal.querySelector('[data-qop-cart-body]');
    var skipEl = cartModal.querySelector('[data-qop-cart-skipped]');
    if (titleEl) titleEl.textContent = 'You already have ' + plan.cartCount + (plan.cartCount === 1 ? ' item' : ' items') + ' in your cart';
    if (bodyEl) bodyEl.innerHTML = _buildCartModalBody(plan);
    if (skipEl) {
      var notices = [];
      if (plan.skipped.length) {
        notices.push(plan.skipped.length + (plan.skipped.length === 1 ? ' item is' : ' items are') +
          ' out of stock and will not be added: ' + plan.skipped.map(function (s) { return s.title; }).join(', '));
      }
      // C3: items "Add to my cart" (merge) can't fully add because the cart
      // already holds most/all of the available stock for that line,
      // surfaced the same way as an out-of-stock skip.
      var mergeTrimmed = (_mergeCartPlan.addable || []).filter(function (a) { return a.trimmed; });
      var mergeBlocked = (_mergeCartPlan.skipped || []).filter(function (s) {
        return !plan.skipped.some(function (p) { return p.variantId === s.variantId; });
      });
      var mergeNames = mergeTrimmed.map(function (a) { return a.title; }).concat(mergeBlocked.map(function (s) { return s.title; }));
      if (mergeNames.length) {
        notices.push('"Add to my cart" will not fully add ' + mergeNames.length + (mergeNames.length === 1 ? ' item' : ' items') +
          ' already near its cart limit: ' + mergeNames.join(', '));
      }
      if (notices.length) { skipEl.textContent = notices.join(' '); skipEl.hidden = false; }
      else { skipEl.hidden = true; }
    }
    cartModal.hidden = false;
    var dialog = cartModal.querySelector('.of-qop__cart-dialog');
    if (dialog) dialog.focus();
  }

  // POST the add. Two modes:
  //  - "merge": single /cart/add.js with every addable item (increments any
  //    overlapping cart lines by the pad qty; caller passes the C3-capped
  //    mergePlan so this can never exceed inventory room).
  //  - "replace": set ABSOLUTE quantities, keyed by cart line item KEY (never
  //    variant id, C1) so a variant split across multiple cart lines
  //    (custom properties, selling plans) only ever has its own plain line
  //    touched: /cart/update.js FIRST zeros every existing line then sets any
  //    overlapping pad item's OWN key to exactly the pad qty; then
  //    /cart/add.js adds ONLY the genuinely new pad items (no key, they
  //    were never included in the update.js payload, so they can't be
  //    double-added).
  async function submitToCart(mode, plan) {
    if (!plan || !plan.addable.length) return;
    var label = $('.of-qop__add-all-label');
    var old = label ? label.textContent : '';
    addAllBtn.setAttribute('aria-disabled', 'true');
    if (label) label.textContent = 'Adding...';
    var step1Done = false;
    // C6: actual completed rows, not the planned count, 0 on a clean
    // failure, the true landed count on a "replace" partial, the full
    // planned count only once every step has actually succeeded.
    var rowsCompleted = 0;
    var totalUnits = plan.addable.reduce(function (s, a) { return s + (a.qty || 0); }, 0);
    try {
      if (mode === 'replace') {
        // 1) Set absolute quantities on existing cart lines: zero every
        //    existing line by its own key, then set any overlapping pad
        //    item's matched key to exactly the pad qty.
        var updates = {};
        plan.cartLines.forEach(function (c) { if (c.key) updates[c.key] = 0; });
        plan.addable.forEach(function (a) { if (a.key) updates[a.key] = a.qty; });
        var upd = await fetch('/cart/update.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: updates }),
        });
        if (!upd.ok) {
          var udesc = '';
          try { var ub = await upd.json(); udesc = (ub && (ub.description || ub.message)) || ''; } catch (_) {}
          throw new Error(udesc || ('cart/update ' + upd.status));
        }
        step1Done = true;
        var existingCount = plan.addable.filter(function (a) { return !a.isNew; }).length;
        rowsCompleted = existingCount;
        // 2) Add ONLY genuinely new pad items (no matched key, step 1 never
        //    touched them), exactly once.
        var newItems = plan.addable.filter(function (a) { return a.isNew === true; });
        if (newItems.length) {
          var addRes = await fetch('/cart/add.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: newItems.map(function (a) { return { id: a.variantId, quantity: a.qty }; }) }),
          });
          if (!addRes.ok) {
            var desc = '';
            try { var b = await addRes.json(); desc = (b && (b.description || b.message)) || ''; } catch (_) {}
            throw new Error(desc || ('cart/add ' + addRes.status));
          }
        }
        rowsCompleted = plan.addable.length;
      } else {
        // merge: single add with every addable item (already C3-capped by the caller).
        var addRes2 = await fetch('/cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: plan.addable.map(function (a) { return { id: a.variantId, quantity: a.qty }; }) }),
        });
        if (!addRes2.ok) {
          var desc2 = '';
          try { var b2 = await addRes2.json(); desc2 = (b2 && (b2.description || b2.message)) || ''; } catch (_) {}
          throw new Error(desc2 || ('cart/add ' + addRes2.status));
        }
        rowsCompleted = plan.addable.length;
      }
      // C5: a note-update failure must not masquerade as a full success,
      // items landed, but the note the shopper typed didn't, so surface it.
      var noteFailed = false;
      var note = (notesInput.value || '').trim();
      if (note) {
        // F1 (2026-09-06 review): `note` on /cart/update.js REPLACES the whole
        // field, and this pad's note box renders empty, so a bare write wiped
        // a PO number the shopper had typed on the cart page or in the drawer.
        // plan.cartNote carries the note from the same getCart() snapshot the
        // plan was built from; merge onto it instead of overwriting it.
        var mergedNote = Core.mergeCartNote(plan && plan.cartNote, note);
        var noteRes = await fetch('/cart/update.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: mergedNote }),
        });
        if (!noteRes.ok) { noteFailed = true; console.warn('[QOP] cart note update failed', noteRes.status); }
      }
      clearDraft();
      if (window.OFTrack) {
        OFTrack.event('of_quick_order_submit', {
          surface: 'pad', status: noteFailed ? 'partial' : 'success', mode: mode, rows_added: rowsCompleted, total_units: totalUnits,
        });
      }
      if (noteFailed) alert('Your items were added to the cart, but the order note could not be saved. You can add it again on the cart page.');
      window.location.href = '/cart';
    } catch (e) {
      addAllBtn.setAttribute('aria-disabled', 'false');
      if (label) label.textContent = old;
      if (NudgesCore && NudgesCore.invalidateSharedCart) NudgesCore.invalidateSharedCart();
      console.error('[QOP] add-all failed', e);
      if (window.OFTrack) {
        OFTrack.event('of_quick_order_submit', {
          surface: 'pad', status: (mode === 'replace' && step1Done) ? 'partial' : 'failure',
          mode: mode, rows_added: rowsCompleted, total_units: totalUnits,
        });
      }
      alert(e && e.message ? e.message : 'Could not add items to cart. Please try again.');
    }
  }

  // Wire the modal buttons once.
  if (cartModal) {
    var _mergeBtn = cartModal.querySelector('[data-qop-cart-merge]');
    var _replaceBtn = cartModal.querySelector('[data-qop-cart-replace]');
    var _closeBtn = cartModal.querySelector('[data-qop-cart-close]');
    if (_mergeBtn) _mergeBtn.addEventListener('click', function () { closeCartModal(); submitToCart('merge', _mergeCartPlan); });
    if (_replaceBtn) _replaceBtn.addEventListener('click', function () { closeCartModal(); submitToCart('replace', _cartPlan); });
    if (_closeBtn) _closeBtn.addEventListener('click', closeCartModal);
    cartModal.addEventListener('click', function (e) { if (e.target === cartModal) closeCartModal(); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !cartModal.hidden) closeCartModal(); });
  }

  addAllBtn.addEventListener('click', async () => {
    if (addAllBtn.getAttribute('aria-disabled') === 'true') return;
    // C4: disable synchronously, before the getCart() await, a rapid second
    // click during that round trip must not start a second concurrent flow.
    addAllBtn.setAttribute('aria-disabled', 'true');
    var padItems = lines.filter(function (l) { return l.qty > 0; }).map(function (l) {
      return { variantId: l.variantId, qty: l.qty, title: l.title, image: l.image, available: l.available, maxQty: l.maxQty };
    });
    if (!padItems.length) { addAllBtn.setAttribute('aria-disabled', 'false'); return; }
    var cart = { items: [] };
    try { cart = (NudgesCore && NudgesCore.getCart) ? await NudgesCore.getCart() : { items: [] }; } catch (_) {}
    var plan = Core.buildAddAllPlan((cart && cart.items) || [], padItems);
    // F1: thread the cart's EXISTING note through the plan so submitToCart can
    // merge rather than replace it (mergePlan below copies this across).
    plan.cartNote = (cart && cart.note) || '';
    if (!plan.addable.length) {
      addAllBtn.setAttribute('aria-disabled', 'false');
      alert('These items are out of stock and cannot be added right now.');
      return;
    }
    // C3: "Add to my cart" stacks on top of the cart, so its addable list
    // must be capped against room remaining (maxQty - currentQty), not just
    // the pad's own already-resolved maxQty. "Replace my cart" sets ABSOLUTE
    // quantities, so it keeps the original (uncapped-by-cart-contents) plan.
    var mergeCap = Core.capForMerge(plan.addable);
    var mergePlan = Object.assign({}, plan, { addable: mergeCap.addable, skipped: plan.skipped.concat(mergeCap.skipped) });
    if (window.OFTrack) {
      var attemptUnits = plan.addable.reduce(function (s, a) { return s + (a.qty || 0); }, 0);
      OFTrack.event('of_quick_order_submit_attempt', { surface: 'pad', rows_count: plan.addable.length, total_units: attemptUnits });
    }
    if (plan.cartEmpty) { submitToCart('merge', mergePlan); return; } // submitToCart owns disable/enable from here
    openAddAllModal(plan, mergePlan); // modal buttons + closeCartModal() re-enable
  });

  // ---- Draft persistence (localStorage) ----
  const DRAFT_KEY = 'of_quick_order_draft_v1';
  function saveDraft() {
    try {
      const payload = {
        lines: lines.filter((l) => l.qty > 0).map((l) => ({
          variantId: l.variantId, productId: l.productId, handle: l.handle,
          sku: l.sku, title: l.title, image: l.image, imageLarge: l.imageLarge,
          priceCents: l.priceCents, qty: l.qty, ruleSlug: l.ruleSlug, ruleLabel: l.ruleLabel,
          available: l.available, availableCount: l.availableCount, maxQty: l.maxQty,
        })),
        note: (notesInput.value || ''),
        // S6: which saved order (if any) is open, so a later boot-time
        // arbitration that picks THIS draft's content never re-pairs it with
        // a stale/foreign account activeId.
        activeId: _activeId,
        activeName: _activeName,
        // Stamped so of-quick-order-saved.js's boot-time freshness arbitration
        // (OFQuickOrderSavedCore.pickFresherEnvelope) can tell this draft apart
        // from a stale account `current` snapshot instead of always trusting
        // the account copy. S2: while _pinnedStamp is set (a read-only mirror,
        // not a genuine edit), reuse the SOURCE's own original stamp instead
        // of manufacturing a fresh Date.now() that would cement whichever
        // side happened to render first.
        updatedAt: _pinnedStamp != null ? _pinnedStamp : Date.now(),
      };
      localStorage.setItem(DRAFT_KEY, JSON.stringify(payload));
    } catch (_) {}
  }
  function clearDraft() { try { localStorage.removeItem(DRAFT_KEY); } catch (_) {} }
  function restoreDraft() {
    let payload;
    try { payload = JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null'); } catch (_) { payload = null; }
    if (!payload || !Array.isArray(payload.lines)) return;
    payload.lines.forEach((l) => {
      const rowEl = makeRow();
      const line = Object.assign({}, l, { rowEl });
      lines.push(line);
      fillRow(rowEl, line);
    });
    if (payload.note) notesInput.value = payload.note;
    _activeId = payload.activeId || null;
    _activeName = _activeId ? (payload.activeName || null) : null;
    // S2: this is a read-only mirror of the draft's OWN last-saved content,
    // not a fresh edit, preserve its original stamp so simply loading the
    // page can't manufacture artificial freshness for cross-device
    // arbitration (of-quick-order-saved.js's 6b reads this back via getDraft()).
    _pinnedStamp = (typeof payload.updatedAt === 'number' && payload.updatedAt > 0) ? payload.updatedAt : null;
  }

  // ---- Group-discount dot popover (tier ladder + live progress) ----
  // Pad-level pooled qty for a rule (shared by the picker preview too).
  function padQtyForSlug(slug) {
    return lines.reduce((s, l) => s + (l.ruleSlug === slug ? (l.qty || 0) : 0), 0);
  }
  let _dotPop = null;
  function ensureDotPop() {
    if (_dotPop) return _dotPop;
    _dotPop = document.createElement('div');
    _dotPop.className = 'of-qop__dot-pop';
    _dotPop.setAttribute('role', 'tooltip');
    _dotPop.hidden = true;
    document.body.appendChild(_dotPop);
    return _dotPop;
  }
  function hideDotPopover() { if (_dotPop) _dotPop.hidden = true; }
  function showDotPopover(dot, slug) {
    const rule = RULE_MAP[slug];
    if (!rule || !rule.tiers || !rule.tiers.length) return;
    const pooled = (cartPool[slug] || 0) + padQtyForSlug(slug);
    const pct = Core.activeTierForQty(pooled, rule.tiers);
    const tiers = rule.tiers.slice().sort((a, b) => a.qty - b.qty);
    let currentIdx = -1;
    tiers.forEach((t, i) => { if (pooled >= t.qty) currentIdx = i; });
    const step = (NudgesCore && NudgesCore.nextTier) ? NudgesCore.nextTier(pooled, rule.tiers) : { next: null };
    let ladder = '';
    tiers.forEach((t, i) => {
      const state = i < currentIdx ? 'is-done' : (i === currentIdx ? 'is-current' : '');
      const mark = i <= currentIdx ? '&#10003;' : '&#9675;';
      ladder += '<li class="' + state + '"><span class="of-qop__dot-pop-mark">' + mark + '</span>' + t.qty + '+ &rarr; ' + t.pct + '% off</li>';
    });
    let next = '';
    if (step && step.next && (step.next.qty - pooled) > 0) {
      next = '<div class="of-qop__dot-pop-next">Add ' + (step.next.qty - pooled) + ' more for ' + step.next.pct + '% off</div>';
    } else if (currentIdx === tiers.length - 1) {
      next = '<div class="of-qop__dot-pop-next">Top tier reached. Max discount unlocked.</div>';
    }
    const pop = ensureDotPop();
    pop.style.setProperty('--of-qop-disc-fg', rule.fg || '#1f2937'); // of-keep: text on a fill, dark block gives it ink (plan carry-in)
    pop.innerHTML =
      '<div class="of-qop__dot-pop-title">' + escapeHtml(rule.cartLabel || '') + '</div>' +
      '<div class="of-qop__dot-pop-have">You have ' + pooled + (cartPool[slug] ? ' between this list and your cart' : ' on this list') + (pct > 0 ? ' &middot; ' + pct + '% off now' : '') + '</div>' +
      '<ul class="of-qop__dot-pop-tiers">' + ladder + '</ul>' + next;
    pop.hidden = false;
    const r = dot.getBoundingClientRect();
    const pr = pop.getBoundingClientRect();
    let left = r.left + window.scrollX;
    let top = r.top + window.scrollY - pr.height - 8;
    if (top < window.scrollY + 4) top = r.bottom + window.scrollY + 8; // flip below if no room above
    const maxLeft = window.scrollX + document.documentElement.clientWidth - pr.width - 8;
    if (left > maxLeft) left = maxLeft;
    if (left < window.scrollX + 4) left = window.scrollX + 4;
    pop.style.left = left + 'px';
    pop.style.top = top + 'px';
  }

  // ---- Boot ----
  restoreDraft();
  ensureTrailingEmptyRow();
  recompute();

  // ---- Cart pooling: tally per-rule quantities already in the cart ----
  // /cart.js items carry no tags, so resolve each item's tags via getProduct()
  // (cached) and match to rules, same rule logic as the rest of the pad.
  async function rebuildCartPool() {
    if (!NudgesCore || !NudgesCore.getCart) return;
    try {
      const cart = await NudgesCore.getCart();
      const next = {};
      const items = (cart && cart.items) || [];
      for (const it of items) {
        const handle = it.handle || (it.url ? it.url.split('?')[0].split('/').pop() : '');
        if (!handle) continue;
        const pj = await NudgesCore.getProduct(handle);
        if (!pj) continue;
        const tags = NudgesCore.normalizeProductTags(pj);
        for (const r of RULES) {
          if (RULE_MAP[r.slug] && RULE_MAP[r.slug].tiers.length && NudgesCore.matches(tags, r)) {
            next[r.slug] = (next[r.slug] || 0) + (it.quantity || 0);
          }
        }
      }
      cartPool = next;
      recompute();
      // The list editor's rows pool with the cart the same way (2026-10-26).
      try { document.dispatchEvent(new CustomEvent('qop:cartpool')); } catch (_) {}
    } catch (_) { /* leave cartPool as-is; fall back to pad-only pooling */ }
  }
  rebuildCartPool();
  const _cartRefresh = (NudgesCore && NudgesCore.debounce) ? NudgesCore.debounce(rebuildCartPool, 400) : rebuildCartPool;
  window.addEventListener('focus', _cartRefresh);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) _cartRefresh(); });

  // Lightbox for row thumbnails (mirrors snippets/quick-order-list.liquid).
  // Full-viewport image; closes via the X, a tap/click outside the image, or
  // Escape. Minimal focus trap (2026-07-26, Rhett's mobile review): the
  // close button is the only focusable element inside the dialog, so
  // trapping is just "Tab/Shift+Tab always lands back on it," and focus
  // returns to whatever triggered the open on close (the accordion's
  // equivalent lightbox in snippets/quick-order-list.liquid, and this one
  // before this fix, both lacked a trap entirely -- flagged in
  // docs/superpowers/reviews/2026-07-19-full-theme-review.md, U10-11).
  let lightbox = null;
  let _lightboxOpener = null;
  function closeLightbox() {
    if (!lightbox || !lightbox.classList.contains('is-open')) return;
    lightbox.classList.remove('is-open');
    if (_lightboxOpener && typeof _lightboxOpener.focus === 'function') _lightboxOpener.focus();
    _lightboxOpener = null;
  }
  function ensureLightbox() {
    if (lightbox) return lightbox;
    lightbox = document.createElement('div');
    lightbox.className = 'of-qop-lightbox';
    lightbox.setAttribute('role', 'dialog');
    lightbox.setAttribute('aria-modal', 'true');
    lightbox.setAttribute('aria-label', 'Product image');
    lightbox.innerHTML = '<button type="button" class="of-qop-lightbox__close" aria-label="Close">&times;</button><img class="of-qop-lightbox__img" alt="">';
    document.body.appendChild(lightbox);
    lightbox.addEventListener('click', (e) => { if (e.target === lightbox || e.target.closest('.of-qop-lightbox__close')) closeLightbox(); });
    document.addEventListener('keydown', (e) => {
      if (!lightbox.classList.contains('is-open')) return;
      if (e.key === 'Escape') { closeLightbox(); return; }
      if (e.key === 'Tab') {
        // Only one focusable element inside (the close button, the image
        // itself isn't focusable), so trapping is just "keep focus there."
        e.preventDefault();
        lightbox.querySelector('.of-qop-lightbox__close').focus();
      }
    });
    return lightbox;
  }
  root.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qop-thumb-btn]');
    if (!btn || !btn.dataset.imgFull) return;
    const lb = ensureLightbox();
    lb.querySelector('.of-qop-lightbox__img').src = btn.dataset.imgFull;
    _lightboxOpener = btn;
    lb.classList.add('is-open');
    lb.querySelector('.of-qop-lightbox__close').focus();
    if (window.OFTrack) OFTrack.event('of_quick_order_row_action', { surface: 'pad', action: 'zoom', sku: btn.dataset.sku || '' });
  });
  // "View product" link: fire-and-let-navigate. target=_blank means the pad
  // itself never unloads, so the click listener always completes.
  root.addEventListener('click', (e) => {
    const link = e.target.closest('[data-qop-view-link]');
    if (!link || link.hidden || !window.OFTrack) return;
    OFTrack.event('of_quick_order_row_action', { surface: 'pad', action: 'view_product', sku: link.dataset.sku || '' });
  });

  // Saved-pad resolve failure handling.
  // resolveVariants() resolves {} when the Storefront fetch fails (it never
  // rejects), while a successful parse stubs EVERY requested id (unknown ids
  // get exists:false). So a loaded line whose variantId is missing from the
  // returned map means the lookup itself failed, not that the variant is gone.
  // Those rows keep priceCents 0; mark them and offer a retry instead of
  // silently showing $0.00 totals.
  let resolveNotice = null;
  function ensureResolveNotice() {
    if (resolveNotice) return resolveNotice;
    resolveNotice = document.createElement('div');
    resolveNotice.className = 'of-qop__resolve-notice';
    resolveNotice.setAttribute('role', 'status');
    resolveNotice.setAttribute('aria-live', 'polite');
    resolveNotice.hidden = true;
    const msg = document.createElement('span');
    msg.className = 'of-qop__resolve-notice-msg';
    msg.textContent = 'Current prices could not be loaded for some items.';
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'of-qop__resolve-retry';
    retry.textContent = 'Retry';
    retry.addEventListener('click', () => { hideResolveNotice(); resolveLoadedLines(); });
    resolveNotice.appendChild(msg);
    resolveNotice.appendChild(retry);
    const grid = $('[data-qop-grid]');
    if (grid && grid.parentNode) grid.parentNode.insertBefore(resolveNotice, grid.nextSibling);
    else root.appendChild(resolveNotice);
    return resolveNotice;
  }
  function showResolveNotice() { ensureResolveNotice().hidden = false; }
  function hideResolveNotice() { if (resolveNotice) resolveNotice.hidden = true; }

  // Mark a loaded row whose live lookup failed. Reuses the stock-label slot +
  // classes (see applyInventoryToRow); a later successful retry overwrites it.
  // C7: an unresolved lookup must not leave the row silently addable at $0,
  // treat it as unavailable (excluded from buildAddAllPlan, capped from
  // further qty increases, qty zeroed like a genuinely sold-out line) until a
  // retry succeeds.
  function markRowUnresolved(line) {
    if (!line) return;
    line.available = false;
    line.maxQty = 0;
    if (line.qty > 0) line.qty = 0;
    if (!line.rowEl) return;
    var el = line.rowEl.querySelector('[data-qop-stock]');
    if (el) {
      el.textContent = 'Price unavailable';
      el.className = 'of-qop__stock of-qop__stock--none';
      el.hidden = false;
    }
    line.rowEl.classList.add('is-unavailable');
    var q = line.rowEl.querySelector('[data-qop-qty]');
    if (q) q.value = '0';
  }

  // Re-resolve loaded lines against the live catalog: fill current price,
  // flag out-of-stock / discontinued (loaded lines carry priceCents 0).
  // Called from loadPad and from the resolve-failure notice's Retry button.
  function resolveLoadedLines() {
    // R1: capture the generation at call time. If a NEWER loadPad() has since
    // bumped _loadGen by the time this resolves, this response belongs to a
    // pad the shopper isn't even looking at anymore, discard it silently.
    var myGen = _loadGen;
    var ids = lines.filter(function (l) { return l.variantId; }).map(function (l) { return l.variantId; });
    if (!window.OFQuickOrderResolve || !ids.length) return;
    window.OFQuickOrderResolve.resolveVariants(ids, TOKEN, DOMAIN).then(function (map) {
      if (!Core.isCurrentGeneration(myGen, _loadGen)) return; // R1: superseded by a newer load
      var failed = 0;
      lines.forEach(function (l) {
        if (!l.variantId) return; // trailing empty row
        var info = map[l.variantId];
        if (!info) { failed++; markRowUnresolved(l); return; }
        l.priceCents = info.priceCents;
        l.available = info.available && info.exists;
        l.availableCount = (typeof info.qty === 'number' ? info.qty : null);
        if (info.productId) l.productId = info.productId;
        if (info.handle) l.handle = info.handle;
        applyInventoryToRow(l);
        if (info.image) {
          l.image = info.image;
          l.imageLarge = info.imageLarge || info.image;
          if (l.rowEl) {
            var thumbBtn = l.rowEl.querySelector('[data-qop-thumb-btn]');
            var thumb = l.rowEl.querySelector('[data-qop-thumb]');
            if (thumb) thumb.src = l.image;
            if (thumbBtn) { thumbBtn.hidden = false; thumbBtn.dataset.imgFull = l.imageLarge || l.image; thumbBtn.dataset.sku = l.sku || ''; }
          }
        }
        // Saved-pad lines land here with no handle until this live lookup
        // returns one; repaint the "View product" link the same way fillRow()
        // does on first render so a reopened saved order gets it too.
        if (l.rowEl) {
          var viewLink = l.rowEl.querySelector('[data-qop-view-link]');
          if (viewLink) {
            if (l.handle) { viewLink.href = '/products/' + l.handle; viewLink.dataset.sku = l.sku || ''; viewLink.hidden = false; }
            else { viewLink.hidden = true; viewLink.removeAttribute('href'); }
          }
        }
        var infoTagsDown = (info.tags || []).map(function (t) { return String(t).toLowerCase(); });
        var rule = resolveRuleForTags(infoTagsDown);
        l.ruleSlug = rule.slug;
        l.ruleLabel = rule.label;
        // Same raw-tag checks as toResult(): hazardous/free-ship must mirror
        // snippets/of-cart-item-messages.liquid exactly, not the rule-matching
        // tag list (which also folds in productType/vendor).
        l.hazardous = infoTagsDown.indexOf('hazardous item') !== -1;
        l.freeShip = infoTagsDown.indexOf('free shipping specials') !== -1;
        if (l.rowEl) renderRowDiscountInfo(l.rowEl, l);
        if (l.rowEl) l.rowEl.classList.toggle('is-unavailable', !l.available);
      });
      recompute();
      if (failed) showResolveNotice(); else hideResolveNotice();
    }).catch(function (err) {
      if (!Core.isCurrentGeneration(myGen, _loadGen)) return; // R1: superseded by a newer load
      // Defensive: resolveVariants resolves {} on fetch failure, so this fires
      // only if the success handler above throws. Fail visibly, not silently.
      console.warn('[QOP] saved-pad resolve failed', err);
      lines.forEach(function (l) { if (l.variantId && !l.priceCents) markRowUnresolved(l); });
      showResolveNotice();
      recompute();
    });
  }

  // Read/write bridge for the saved-orders module (sibling file).
  window.__OFQuickOrderPad = Object.assign(window.__OFQuickOrderPad || {}, {
    // Quantity price breaks, lent to the list editor (assets/of-lists-pad.js,
    // 2026-10-26: "like Quick Order shows them"): this pad's rule map, its
    // rule for a product's downcased tags, what the cart already holds per
    // rule (rebuilt on focus, and on refresh() after a list adds to the
    // cart; 'qop:cartpool' on document when it changes).
    bulk: {
      rules: RULE_MAP,
      ruleFor: function (tagsDown) { return resolveRuleForTags(tagsDown || []).slug; },
      cartPool: function () { return Object.assign({}, cartPool); },
      refresh: function () { _cartRefresh(); },
    },
    getState: function () {
      return {
        note: (notesInput && notesInput.value) || '',
        lines: lines.filter(function (l) { return l.qty > 0; }).map(function (l) {
          return { variantId: l.variantId, sku: l.sku, title: l.title, qty: l.qty };
        }),
      };
    },
    // Content-only, mirrors getState() but stamped + read from localStorage
    // (not live DOM state) so of-quick-order-saved.js's boot-time freshness
    // arbitration can compare it against the account's `current` envelope
    // without depending on this module's internal `lines`/`notesInput`.
    getDraft: function () {
      try {
        return JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null');
      } catch (_) {
        return null;
      }
    },
    // Bulk-selection module support (additive; saved-orders surface unchanged).
    getResolvedRows: function () {
      // One descriptor per resolved row, in DOM order, for the bulk-selection module.
      return lines.filter(function (l) { return l.rowEl && l.qty > 0; }).map(function (l) {
        return {
          rowEl: l.rowEl,
          variantId: l.variantId,
          productId: l.productId || null,
          handle: l.handle || '',
          title: l.title,
        };
      });
    },
    // Lists phase 2 (2026-10-22): the pad's own part number search, for
    // "Swap in a replacement" on a list row we no longer stock
    // (assets/of-lists-pad.js). Resolves ranked results; rejects with
    // isNetworkError when every search form failed.
    resolvePart: function (raw) { return resolveInput(raw); },
    removeRowEls: function (rowEls) {
      // Remove a set of rows via the canonical removeRow (DOM + lines + recompute).
      (rowEls || []).forEach(function (el) { if (el) removeRow(el); });
    },
    // S6: mirror active-order identity (set by of-quick-order-saved.js's
    // setActivePad()) into the module and persist it into the draft
    // immediately, so a later boot-time arbitration that picks THIS draft's
    // content never re-pairs it with a stale/foreign account activeId.
    setActiveIdentity: function (id, name) {
      _activeId = id || null;
      _activeName = _activeId ? (name || null) : null;
      saveDraft();
    },
    // opts.preserveStamp (S2): a boot-time MIRROR of an already-authoritative
    // envelope (the account's `current`, or the draft's own boot restore) is
    // a READ, not a user edit, pin the draft's stamp to the source's
    // original updatedAt instead of Date.now(), and suppress the qop:changed
    // signal (see recompute()) so it's never written back to the account.
    // Genuine loads (Start new / Open a saved order) omit opts entirely.
    loadPad: function (saved, opts) {
      opts = opts || {};
      _loadGen++; // R1: invalidate any in-flight resolveLoadedLines from a prior load
      // Replace pad contents with a saved pad's lines + note.
      var _existing = rowsHost.querySelectorAll('[data-qop-row]');
      for (var _i = 0; _i < _existing.length; _i++) _existing[_i].remove();
      lines = [];
      (saved && saved.lines || []).forEach(function (l) {
        var rowEl = makeRow();
        var line = { variantId: l.variantId, sku: l.sku, title: l.title, image: '', imageLarge: '',
                     priceCents: 0, qty: l.qty, ruleSlug: '', ruleLabel: '', available: true, availableCount: null, maxQty: 999, rowEl: rowEl };
        lines.push(line); fillRow(rowEl, line);
      });
      if (notesInput) notesInput.value = (saved && saved.note) || '';
      _pinnedStamp = (typeof opts.preserveStamp === 'number' && opts.preserveStamp > 0) ? opts.preserveStamp : null;
      ensureTrailingEmptyRow(); recompute(); // recompute() dispatches qop:changed only when NOT pinned
      // Re-resolve loaded lines against the live catalog (loaded lines carry
      // priceCents 0). Clears any stale failure notice from a previous load.
      hideResolveNotice();
      resolveLoadedLines();
    },
  });
})();
